/**
 * SIP Interface editor (tabbed modal).
 *
 * Replaces the previous hand-rolled modal in SipInterfacesPage with the shared
 * ResourceEditor. Tabs: General, NAT Traversal, Manipulation, and a TLS tab
 * that is only shown when the selected protocol is TLS.
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Settings, Globe, ShieldCheck, Workflow } from 'lucide-react';
import {
  SipInterfaceCreateInputSchema,
  type SipInterfaceCreateInput,
} from '@sbclite/core/schemas';
import {
  useCreateSipInterface,
  useUpdateSipInterface,
  type SipInterface,
  type SipInterfaceCreate,
} from '../../hooks/useSipInterfaces';
import { useSipManipulationsForDropdown } from '../../hooks/useSipManipulations';
import {
  useServerCertificatesForDropdown,
  useCACertificatesForDropdown,
} from '../../hooks/useCertificates';
import { useTlsContexts } from '../../hooks/useTlsContexts';
import { useHosts } from '../../hooks/useHosts';
import { createFormErrorHandler } from '../../utils/form-error-handler';
import { isValidIpv4 } from '../../utils/ip-validation';
import { ResourceEditor, firstTabWithError, type ResourceEditorTab } from '../common';

type SipInterfaceFormData = SipInterfaceCreateInput;

function buildDefaultValues(editingInterface?: SipInterface | null): SipInterfaceFormData {
  return {
    interfaceId: editingInterface?.interfaceId || '',
    port: editingInterface?.port || 5060,
    protocol: editingInterface?.protocol || 'UDP',
    bindAddress: editingInterface?.bindAddress || '0.0.0.0',
    advertisedAddress: editingInterface?.advertisedAddress || null,
    hostId: editingInterface?.hostId || null,
    tlsContextId: editingInterface?.tlsContextId || null,
    preClassificationManipulationId: editingInterface?.preClassificationManipulationId || null,
    enabled: editingInterface?.enabled ?? true,
    tlsConfig: editingInterface?.tlsConfig || null,
    natTraversalConfig: editingInterface?.natTraversalConfig || { mode: 'auto', forceRport: false },
    challengeUnmatchedOptions: editingInterface?.challengeUnmatchedOptions ?? false,
    maxConnections: editingInterface?.maxConnections ?? undefined,
    maxConcurrentCalls: editingInterface?.maxConcurrentCalls ?? undefined,
    description: editingInterface?.description || null,
    tags: editingInterface?.tags || null,
  };
}

interface SipInterfaceEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingInterface?: SipInterface | null;
}

export function SipInterfaceEditor({ isOpen, onClose, onSuccess, editingInterface }: SipInterfaceEditorProps) {
  const [activeTab, setActiveTab] = useState('general');

  const formErrorHandler = createFormErrorHandler({
    componentName: 'SipInterfaceEditor',
    showFieldErrors: true,
  });

  const form = useForm<SipInterfaceFormData>({
    resolver: zodResolver(SipInterfaceCreateInputSchema),
    defaultValues: buildDefaultValues(editingInterface),
  });

  const protocol = form.watch('protocol');

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildDefaultValues(editingInterface));
    setActiveTab('general');
  }, [isOpen, editingInterface, form]);

  // Seed a sensible TLS config when switching a new interface to TLS.
  useEffect(() => {
    if (protocol === 'TLS' && !editingInterface) {
      const currentTlsConfig = form.getValues('tlsConfig');
      if (!currentTlsConfig) {
        form.setValue(
          'tlsConfig',
          { certificateId: '', requireClientCert: true, verifyClient: true },
          { shouldDirty: true }
        );
      }
    }
  }, [protocol, editingInterface, form]);

  // Clear TLS config when protocol changes away from TLS.
  useEffect(() => {
    if (protocol !== 'TLS') {
      form.setValue('tlsConfig', null);
    }
  }, [protocol, form]);

  const createMutation = useCreateSipInterface();
  const updateMutation = useUpdateSipInterface();

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
    form.reset(buildDefaultValues(null));
    setActiveTab('general');
  };

  const onSubmit = async (data: SipInterfaceFormData) => {
    try {
      // Strip null/undefined/empty values so optional fields are omitted.
      const cleanData = Object.entries(data).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {}) as SipInterfaceFormData;

      if (data.protocol !== 'TLS') {
        cleanData.tlsConfig = null;
      }

      if (editingInterface) {
        await updateMutation.mutateAsync({ id: editingInterface.id, data: cleanData });
      } else {
        await createMutation.mutateAsync(cleanData as SipInterfaceCreate);
      }
      handleSuccess();
    } catch (error) {
      formErrorHandler.onError(error);
    }
  };

  const tabs: ResourceEditorTab[] = useMemo(
    () => [
      {
        id: 'general',
        label: 'General',
        icon: Settings,
        fields: [
          'interfaceId',
          'port',
          'protocol',
          'bindAddress',
          'advertisedAddress',
          'hostId',
          'maxConnections',
          'maxConcurrentCalls',
          'enabled',
          'challengeUnmatchedOptions',
          'description',
          'tags',
        ],
        render: () => <GeneralTab form={form} editingInterface={editingInterface} />,
      },
      {
        id: 'nat',
        label: 'NAT Traversal',
        icon: Globe,
        fields: ['natTraversalConfig'],
        render: () => <NatTab form={form} />,
      },
      {
        id: 'tls',
        label: 'TLS',
        icon: ShieldCheck,
        fields: ['tlsConfig'],
        hidden: protocol !== 'TLS',
        render: () => <TlsTab form={form} />,
      },
      {
        id: 'manipulation',
        label: 'Manipulation',
        icon: Workflow,
        fields: ['preClassificationManipulationId'],
        render: () => <ManipulationTab form={form} editingInterface={editingInterface} />,
      },
    ],
    [form, editingInterface, protocol]
  );

  const onInvalid = (errors: typeof form.formState.errors) => {
    const target = firstTabWithError(errors, tabs);
    if (target) setActiveTab(target);
    formErrorHandler.onInvalid(errors);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <ResourceEditor
      isOpen={isOpen}
      onClose={onClose}
      title={editingInterface ? 'Edit SIP Interface' : 'Create SIP Interface'}
      subtitle="Configure network-level SIP interface settings"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      errors={form.formState.errors}
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      onCancel={onClose}
      isSaving={isSaving}
      saveLabel={editingInterface ? 'Update Interface' : 'Create Interface'}
      savingLabel={editingInterface ? 'Updating...' : 'Creating...'}
    />
  );
}

function GeneralTab({
  form,
  editingInterface,
}: {
  form: UseFormReturn<SipInterfaceFormData>;
  editingInterface?: SipInterface | null;
}) {
  const { data: hostsResponse, isLoading: hostsLoading } = useHosts();
  const hosts = hostsResponse?.data || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Interface ID *</label>
          <input
            {...form.register('interfaceId')}
            type="text"
            disabled={!!editingInterface}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            placeholder="e.g., sip-tls-5061"
          />
          {form.formState.errors.interfaceId && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.interfaceId.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Port *</label>
          <input
            {...form.register('port', { valueAsNumber: true })}
            type="number"
            min="1024"
            max="65535"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {form.formState.errors.port && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.port.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Protocol *</label>
          <select
            {...form.register('protocol')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="UDP">UDP</option>
            <option value="TCP">TCP</option>
            <option value="TLS">TLS (Encrypted)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bind Address</label>
          <input
            {...form.register('bindAddress', {
              validate: (value) =>
                isValidIpv4(value) || 'Enter a valid IPv4 address (e.g. 0.0.0.0)',
            })}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.0.0.0"
          />
          {form.formState.errors.bindAddress && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.bindAddress.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Advertised Address</label>
          <input
            {...form.register('advertisedAddress')}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Public IP or DNS name required by the engine"
          />
          {form.formState.errors.advertisedAddress && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.advertisedAddress.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SIP Identity Binding</label>
          <select
            {...form.register('hostId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={hostsLoading}
          >
            <option value="">{hostsLoading ? 'Loading hosts...' : 'Use default host'}</option>
            {hosts.map((host) => (
              <option key={host.id} value={host.id}>
                {host.name} {host.domain ? `(${host.domain})` : ''}{host.isDefault ? ' - Default' : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Max Connections</label>
          <input
            {...form.register('maxConnections', { valueAsNumber: true })}
            type="number"
            min="1"
            max="100000"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="10000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Max Concurrent Calls</label>
          <input
            {...form.register('maxConcurrentCalls', { valueAsNumber: true })}
            type="number"
            min="1"
            max="50000"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="5000"
          />
        </div>
      </div>

      {/* Enabled / challenge */}
      <div className="space-y-3">
        <label className="flex items-center text-sm text-gray-700">
          <input
            {...form.register('enabled')}
            type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
          />
          Enable this interface
        </label>
        <label className="flex items-start text-sm text-gray-700">
          <input
            {...form.register('challengeUnmatchedOptions')}
            type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2 mt-0.5"
          />
          <span>
            Challenge unmatched OPTIONS
            <span className="block text-xs text-gray-500">
              Return 401 instead of generic 200 OK for every unmatched OPTIONS on this interface.
            </span>
          </span>
        </label>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          {...form.register('description')}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Optional description for this interface"
        />
      </div>
    </div>
  );
}

function NatTab({ form }: { form: UseFormReturn<SipInterfaceFormData> }) {
  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900">NAT Traversal (RFC 3581)</h4>
        </div>
        <p className="text-xs text-gray-500">
          Controls symmetric response routing (rport/received). &quot;Auto&quot; is RFC-compliant and
          adds the parameters only when needed.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mode</label>
            <select
              {...form.register('natTraversalConfig.mode')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="auto">Auto (RFC 3581 — recommended)</option>
              <option value="always">Always (aggressive NAT handling)</option>
              <option value="never">Never (trusted internal networks only)</option>
            </select>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center text-sm text-gray-700">
              <input type="checkbox" className="mr-2" {...form.register('natTraversalConfig.forceRport')} />
              Force rport even if the client did not request it
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function TlsTab({ form }: { form: UseFormReturn<SipInterfaceFormData> }) {
  const { data: serverCertificates, isLoading: serverCertsLoading } = useServerCertificatesForDropdown();
  const { data: caCertificates, isLoading: caCertsLoading } = useCACertificatesForDropdown();
  const { data: tlsContexts = [], isLoading: tlsContextsLoading } = useTlsContexts();
  const selectedTlsContextId = form.watch('tlsContextId');
  const selectedTlsContext = tlsContexts.find((context) => context.id === selectedTlsContextId) ?? null;

  return (
    <div className="space-y-6">
      <div className="bg-green-50 p-4 rounded-lg space-y-4 border border-green-200">
        <h4 className="font-medium text-gray-900 flex items-center">
          <ShieldCheck className="w-5 h-5 mr-2 text-green-600" />
          TLS Configuration
        </h4>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">TLS Context *</label>
          <select
            {...form.register('tlsContextId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            disabled={tlsContextsLoading}
          >
            <option value="">{tlsContextsLoading ? 'Loading TLS contexts...' : 'Select a TLS context'}</option>
            {tlsContexts.map((context) => (
              <option key={context.id} value={context.id}>
                {context.name}{context.enabled ? '' : ' (disabled)'}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Reusable server-side TLS policy. TLS interfaces require an enabled context.
          </p>
          {selectedTlsContext ? (
            <p className="mt-1 text-xs text-green-800">Attached context: {selectedTlsContext.name}</p>
          ) : (
            <p className="mt-1 text-xs text-amber-700">No TLS context selected yet.</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Server Certificate *</label>
            <select
              {...form.register('tlsConfig.certificateId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={serverCertsLoading}
            >
              <option value="">Select a server certificate</option>
              {serverCertificates?.map((cert) => (
                <option key={cert.value} value={cert.value}>
                  {cert.label} {cert.teamsCompatible && '(Teams Compatible)'}
                </option>
              ))}
            </select>
            {serverCertsLoading && <p className="text-xs text-gray-500 mt-1">Loading certificates...</p>}
            {form.formState.errors.tlsConfig?.certificateId && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.tlsConfig.certificateId.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CA Certificate (for client verification)
            </label>
            <select
              {...form.register('tlsConfig.caCertificateId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={caCertsLoading}
            >
              <option value="">No CA certificate (optional)</option>
              {caCertificates?.map((cert) => (
                <option key={cert.value} value={cert.value}>
                  {cert.label}
                </option>
              ))}
            </select>
            {caCertsLoading && <p className="text-xs text-gray-500 mt-1">Loading CA certificates...</p>}
            {form.formState.errors.tlsConfig?.caCertificateId && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.tlsConfig.caCertificateId.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Minimum TLS Version</label>
            <select
              {...form.register('tlsConfig.minVersion')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Default</option>
              <option value="TLSv1.2">TLS 1.2</option>
              <option value="TLSv1.3">TLS 1.3</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Maximum TLS Version</label>
            <select
              {...form.register('tlsConfig.maxVersion')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Default</option>
              <option value="TLSv1.2">TLS 1.2</option>
              <option value="TLSv1.3">TLS 1.3</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (seconds)</label>
            <input
              {...form.register('tlsConfig.sessionTimeout', { valueAsNumber: true })}
              type="number"
              min="60"
              max="86400"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="300"
            />
          </div>
        </div>

        {/* TLS checkboxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="flex items-center">
            <input
              {...form.register('tlsConfig.requireClientCert')}
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">Require client certificate</label>
          </div>

          <div className="flex items-center">
            <input
              {...form.register('tlsConfig.verifyClient')}
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">Verify client certificate</label>
          </div>

          <div className="flex items-center">
            <input
              {...form.register('tlsConfig.honorCipherOrder')}
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">Honor server cipher order</label>
          </div>

          <div className="flex items-center">
            <input
              {...form.register('tlsConfig.teamsCompatible')}
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">Microsoft Teams compatible</label>
          </div>

          <div className="flex items-center">
            <input
              {...form.register('tlsConfig.enableTrace')}
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">Enable TLS debugging</label>
          </div>
        </div>

        {(() => {
          const tlsConfig = form.watch('tlsConfig');
          const needsCa =
            !!tlsConfig?.requireClientCert || !!tlsConfig?.rejectUnauthorized || !!tlsConfig?.verifyClient;
          const missingCa = !tlsConfig?.caCertificateId;

          if (!needsCa || !missingCa) return null;

          return (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Client certificate verification is enabled. Select a CA certificate unless your server certificate
              bundle already includes the client CA chain.
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function ManipulationTab({
  form,
  editingInterface,
}: {
  form: UseFormReturn<SipInterfaceFormData>;
  editingInterface?: SipInterface | null;
}) {
  const { data: manipulationsResponse, isLoading } = useSipManipulationsForDropdown();
  const manipulations = manipulationsResponse?.data ?? [];
  const selectedId = form.watch('preClassificationManipulationId');

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
        <h4 className="font-medium text-gray-900 flex items-center">
          <Workflow className="w-5 h-5 mr-2 text-amber-600" />
          Pre-classification manipulation
        </h4>
        <p className="text-xs text-gray-600">
          Applied before source classification. Keep trusted-header behavior explicit and avoid hidden fallbacks.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Manipulation profile</label>
          <select
            {...form.register('preClassificationManipulationId')}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            <option value="">{isLoading ? 'Loading manipulation profiles...' : 'No pre-classification manipulation'}</option>
            {manipulations.map((manipulation) => (
              <option key={manipulation.id} value={manipulation.id}>
                {manipulation.name}{manipulation.enabled ? '' : ' (disabled)'}
              </option>
            ))}
          </select>
          {selectedId ? (
            <p className="mt-1 text-xs text-amber-800">Selected profile ID: {selectedId}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">No profile selected.</p>
          )}
          {editingInterface?.preClassificationManipulationId ? (
            <p className="mt-1 text-xs text-gray-500">Current attachment: {editingInterface.preClassificationManipulationId}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SipInterfaceEditor;
